import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isAfter, isBefore, subMinutes } from "date-fns";

import { consultationsQueriesStore } from "@/features/consultations/api";
import { useBoolean } from "@/shared/hooks";

const MINUTES_BEFORE_CALL_STARTS = 10;
const CHECK_INTERVAL_DURATION = 1000 * 60; // Check every minute

export const useCanJoinConsultation = (
  startTime: string,
  endTime: string,
  enabled = true,
) => {
  const {
    value: canJoin,
    setFalse: disableJoin,
    setTrue: enableJoin,
  } = useBoolean(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const start = new Date(startTime);
    const end = new Date(endTime);
    const enableTime = subMinutes(start, MINUTES_BEFORE_CALL_STARTS);

    const updateCanJoin = () => {
      const now = new Date();
      if (isAfter(now, enableTime) && isBefore(now, end)) {
        enableJoin();
      } else {
        disableJoin();
      }

      if (isAfter(now, end)) {
        void queryClient.invalidateQueries({
          queryKey:
            consultationsQueriesStore.consultations.appointments._ctx.list._ctx
              .upcoming.queryKey,
        });
      }
    };

    updateCanJoin();
    const intervalId = setInterval(updateCanJoin, CHECK_INTERVAL_DURATION);

    return () => clearInterval(intervalId); // Cleanup interval on unmount
  }, [startTime, endTime, enabled, queryClient, enableJoin, disableJoin]);

  return { canJoin };
};
